import AddSupplier from "./add-supplier";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from "@/components/ui/table";
import {
  CaretSortIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { Route as supplierRoute } from "../index";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { useNavigate } from "@tanstack/react-router";
import { SetStateAction, useEffect, useState } from "react";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { SearchIcon } from "lucide-react";
import { useSuppliersData } from "../-hooks/useSuppliersData";

interface Column {
  key: string;
  label: string;
}

const Component = () => {
  const searchParams = supplierRoute.useSearch();
  const navigate = useNavigate();
  const [search, setSearch] = useState(searchParams.search || '');

    // Use the custom hook for data fetching
    const { data: suppliers, isLoading, error, isFetching } = useSuppliersData();

    // Local loading state for search actions
    const [isSearching, setIsSearching] = useState(false);
  
    // Reset search loading when query completes
    useEffect(() => {
      if (!isFetching && isSearching) {
        setIsSearching(false);
      }
    }, [isFetching, isSearching]);
  
    // Helper function to update search params without full navigation
    const updateSearchParams = (updates: Partial<typeof searchParams>, showSearchLoading = false) => {
      if (showSearchLoading) {
        setIsSearching(true);
      }
      
      navigate({
        search: (prev: any) => ({ ...prev, ...updates }),
        replace: true, // This prevents creating new history entries
      });
    };

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("created_at");

  const handleRequestSort = (property: SetStateAction<string>) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = suppliers?.suppliers.slice().sort((a, b) => {
    if (orderBy === "created_at") {
      const dateA = new Date(a.createdAt?.toDate()!).getTime();
      const dateB = new Date(b.createdAt?.toDate()!).getTime();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  const itemsPerPage = searchParams.pageSize
  const totalPages = suppliers?.totalCount
    ? Math.ceil(suppliers.totalCount / itemsPerPage)
    : 1;

  // TableCell and TableHead wrappers for consistent styling
  const StyledTableCell = ({ className = "", ...props }) => (
    <TableCell className={cn(tableCellClass, "font-medium", className)} {...props} />
  );
  const StyledTableHead = ({ className = "", ...props }) => (
    <TableHead className={cn(tableHeadClass, className)} {...props} />
  );

  const columns: Column[] = [
    { key: "name", label: "Name" },
    { key: "contact_details", label: "Contact Details" },
    { key: "credit_allowed", label: "Credit Allowed" },
    { key: "credit_period", label: "Credit Period" },
    { key: "monthly_payment_date", label: "Monthy Payment Date" },
    { key: "created_at", label: "Created" },
  ];

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>)
  );

  return (
    <>
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Suppliers</h2>
            <p className="text-muted-foreground">
              Here is a list of all your suppliers.
            </p>
          </div>
          <AddSupplier />
        </div>
        <hr className="my-4" />
        <div className="flex items-center gap-x-2">
          <div className="relative w-72">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
              <SearchIcon className="w-4 h-4" />
            </span>
            <Input
              placeholder="Search Suppliers..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  updateSearchParams({
                    search: search,
                    page: 1,
                  });
                }
              }}
              className="pl-8 pr-8 w-full"
            />
            {search && (
              <Button
                variant="ghost"
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1 h-6 w-6 text-gray-400 hover:text-gray-700"
                onClick={() => {
                  setSearch("");
                  updateSearchParams({
                    search: "",
                    page: 1,
                  });
                }}
              >
                <span className="sr-only">Clear</span>
                &#10005;
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            disabled={isSearching || isLoading}
            onClick={() => {
              updateSearchParams({
                search: search,
                page: 1,
              }, true);
            }}
          >
            {isSearching || isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                Searching...
              </div>
            ) : (
              'Search'
            )}
          </Button>
        </div>

        <div className="mt-4 border rounded-lg bg-white overflow-hidden">
        {isLoading ? (
              <TableLoading columns={columns} visibleColumns={visibleColumns} />
            ) : !suppliers || suppliers.suppliers.length === 0 ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-sm text-gray-500">No entries found</div>
                </div>
              ) : (
                <>
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <StyledTableHead>Name</StyledTableHead>
                <StyledTableHead>Contact Details</StyledTableHead>
                <StyledTableHead>Credit Allowed(Yes/No)</StyledTableHead>
                <StyledTableHead>Credit Period</StyledTableHead>
                <StyledTableHead>Monthly Payment Date</StyledTableHead>
                <StyledTableHead
                  onClick={() => handleRequestSort("created_at")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="flex items-center">
                    <span>Created At</span>
                    {orderBy === "created_at" && (
                      <CaretSortIcon
                        className={`ml-2 h-4 w-4 ${order === "asc" ? "rotate-180" : ""}`}
                      />
                    )}
                  </div>
                </StyledTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData?.map((supplier, index) => (
                <TableRow 
                  key={index}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate({ 
                    to: '/suppliers/$supplierId',
                    params: { supplierId: supplier.id }
                  })}
                >
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>{supplier.name}</StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {supplier.metadata?.contactPersonName} (
                    {supplier.metadata?.phoneNumber})
                  </StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {supplier.creditTerms?.isCreditAllowed ? "Yes" : "No"}
                  </StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>{supplier.creditTerms?.creditPeriod}</StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {supplier.creditTerms?.monthlyPaymentDay}
                  </StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>{dayjs(supplier.createdAt?.toDate()).format("DD MMM, YYYY")}</StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{suppliers.suppliers && suppliers.suppliers.length > 0 && suppliers.totalCount ? suppliers.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                    onClick={() => {
                      updateSearchParams({
                        page: 1
                      });
                    }}
                disabled={searchParams.page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                    onClick={() => {
                      updateSearchParams({
                        page: searchParams.page - 1
                      });
                    }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (searchParams.page > 3) pages.push('...');
                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (searchParams.page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((p, idx) => (
                  p === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={p}
                        active={searchParams.page === p}
                        onClick={() => {
                          updateSearchParams({
                            page: p as number
                          });
                        }}
                      >
                        <span className="text-sm">{p}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                    onClick={() => {
                      updateSearchParams({
                        page: searchParams.page + 1
                      });
                    }}
                disabled={searchParams.page === totalPages}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                    onClick={() => {
                      updateSearchParams({
                        page: totalPages
                      });
                    }}
                    disabled={searchParams.page === totalPages}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        </>
        )}
        </div>
      </div>
    </>
  );
};

export default Component;
